import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';

import Paper from '@mui/material/Paper';
import { MainTableProps } from '@app/types/main-table';
import { useScrollPosition } from '@hooks/useScrollPosition';
import { useEffect } from 'react';
import MainTableHeadBasic from '@components/MainTable/MainTableHeadBasic';
import MainTablePinnedRow from '@components/MainTable/MainTablePinnedRow';
import MainTableRow from '@components/MainTable/MainTableRow';
import CircularProgress from '@mui/material/CircularProgress';
import MainCard from '@components/ui/cards/MainCard';
import Box from '@mui/material/Box';
import EmptyData from '@components/EmptyData/ui/EmptyData';

export default function MainTableComponent<TData extends Record<string, any>>({
  table,
  dataIds,
  columnOrder,
  isLoading = false,
  showLoadingText = false,
  components,
  onTableRefReady,
  onRowClick,
  noDataTitle = 'No Data Available',
  noDataDescription = 'There is no data to display in this table right now.',
}: MainTableProps<TData>) {
  const { containerRef, tableRef, isLeftScrollable, isRightScrollable } =
    useScrollPosition();

  useEffect(() => {
    if (onTableRefReady) {
      onTableRefReady(tableRef);
    }
  }, [onTableRefReady, tableRef]);

  if (!table) return null;

  if (isLoading) {
    return (
      <MainCard
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
        {showLoadingText ? (
          <Typography variant="h6">Loading...</Typography>
        ) : null}
      </MainCard>
    );
  }

  const headerParam = {
    table,
    dataIds,
    columnOrder,
    isLeftScrollable,
    isRightScrollable,
  };

  const rowParam = {
    table,
    dataIds,
    columnOrder,
    isLeftScrollable,
    isRightScrollable,
  };

  const mainRows = table.getIsSomeRowsPinned()
    ? table.getCenterRows()
    : table.getRowModel().rows;

  return (
    <TableContainer ref={containerRef} component={Paper}>
      <Table ref={tableRef} sx={{ width: '100%' }} aria-label="main-table">
        <>
          {components?.header ? (
            components.header(headerParam)
          ) : (
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => {
                const headerCellParam = {
                  columnOrder,
                  headerGroup,
                  isLeftScrollable,
                  isRightScrollable,
                };
                return (
                  <TableRow key={headerGroup.id}>
                    {components?.headerCell ? (
                      components.headerCell(headerCellParam)
                    ) : (
                      <MainTableHeadBasic
                        headerGroup={headerGroup}
                        isLeftScrollable={isLeftScrollable}
                        isRightScrollable={isRightScrollable}
                      />
                    )}
                  </TableRow>
                );
              })}
            </TableHead>
          )}
        </>
        <TableBody>
          {table.getTopRows().map((row) => (
            <MainTablePinnedRow
              key={row.id}
              row={row}
              table={table}
              isLeftScrollable={isLeftScrollable}
              isRightScrollable={isRightScrollable}
            />
          ))}

          {components?.bodyRow ? (
            components.bodyRow(rowParam)
          ) : (
            <>
              {mainRows.map((row) => (
                <MainTableRow
                  key={row.id}
                  row={row}
                  columnOrder={columnOrder}
                  isLeftScrollable={isLeftScrollable}
                  isRightScrollable={isRightScrollable}
                  components={components}
                  onRowClick={onRowClick}
                />
              ))}
            </>
          )}

          {table.getBottomRows().map((row) => (
            <MainTablePinnedRow
              key={row.id}
              row={row}
              table={table}
              isLeftScrollable={isLeftScrollable}
              isRightScrollable={isRightScrollable}
            />
          ))}
        </TableBody>
      </Table>

      {isEmpty(table.getRowModel().rows) && (
        <Box textAlign="center" paddingY={3}>
          <EmptyData title={noDataTitle} description={noDataDescription} />
        </Box>
      )}
    </TableContainer>
  );
}
